import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography, Link } from "@material-ui/core";
import { SubscriptionDetail } from "api/company";
import moment from "moment";
import i18n from "../i18n";
import { formatter, isActiveSubscription } from "../../../../utils/helpers";

type SubscriptionDetailDialogProps = {
    show: boolean;
    subscriptionDetail: SubscriptionDetail;
    onClose: () => void;
};

const SubscriptionDetailDialog = ({ show, subscriptionDetail, onClose }: SubscriptionDetailDialogProps) => {
    // render meta row
    const renderMetaRow = (label: string, content: React.ReactNode) => (
        <>
            <Grid item xs={4}>
                <Typography>{label}</Typography>
            </Grid>
            <Grid item xs={8}>
                {content}
            </Grid>
        </>
    );

    // link
    const StyledLink = ({ label, url }: { label: string, url: string }) => (
        <Link
            href={url}
            style={{ display: "block" }}
            target="_blank"
        >
            {label}
        </Link>
    );

    return subscriptionDetail && (
        <Dialog open={show} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{`${i18n.subscriptionDetails}`}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="h6" gutterBottom>{i18n.general}</Typography>
                <Grid container spacing={1}>
                    {renderMetaRow(i18n.name, (
                        <Typography>{subscriptionDetail.price.product.name}</Typography>
                    ))}
                    {renderMetaRow(i18n.subscriptionType, (
                        <Typography>{i18n.subscriptionTypes[subscriptionDetail.subscriptionType]}</Typography>
                    ))}
                    {renderMetaRow(i18n.subscriptionStatus, (
                        isActiveSubscription(subscriptionDetail) ? (
                            <Typography>{i18n.subscriptionStatuses[subscriptionDetail.subscriptionStatus]}</Typography>
                        ) : (
                            <Typography>{i18n.cancelled}</Typography>
                        )
                    ))}
                    {renderMetaRow(i18n.noOfseat, (
                        <Typography>{subscriptionDetail.numberOfSeats}</Typography>
                    ))}
                    {renderMetaRow(i18n.currentPeriodStart, (
                        <Typography>{moment.unix(subscriptionDetail.subscriptionInfos.current_period_start).format("DD.MM.YYYY")}</Typography>
                    ))}
                    {renderMetaRow(i18n.currentPeriodEnd, (
                        <Typography>{moment.unix(subscriptionDetail.subscriptionInfos.current_period_end).format("DD.MM.YYYY")}</Typography>
                    ))}
                </Grid>
                <Typography gutterBottom>&nbsp;</Typography>
                <Typography variant="h6" gutterBottom>{i18n.subscriptionInvoice}</Typography>
                <Grid container spacing={1}>
                    {renderMetaRow(i18n.subscriptionInvoiceDescription, (
                        <Typography>{subscriptionDetail.latestInvoice.description}</Typography>
                    ))}
                    {renderMetaRow(i18n.subscriptionInvoiceUrl, (
                        subscriptionDetail.latestInvoice.invoice_pdf ? (
                            <StyledLink label={i18n.subscriptionInvoiceDownload} url={subscriptionDetail.latestInvoice.invoice_pdf} />
                        ) : (
                            <Typography>-</Typography>
                        )
                    ))}
                </Grid>
                <Typography gutterBottom>&nbsp;</Typography>
                <Typography variant="h6" gutterBottom>{i18n.subscriptionCardInfo}</Typography>
                <Grid container spacing={1}>
                    {subscriptionDetail.card?.funding && renderMetaRow(i18n.subscriptionCardFunding, (
                        <Typography>{subscriptionDetail.card?.funding}</Typography>
                    ))}
                    {subscriptionDetail.card?.last4 && renderMetaRow(i18n.subscriptionCard4, (
                        <Typography>{subscriptionDetail.card?.last4}</Typography>
                    ))}
                </Grid>
                <Typography gutterBottom>&nbsp;</Typography>
                <Typography variant="h6" gutterBottom>{i18n.subscriptionUpInvoice}</Typography>
                {subscriptionDetail.upcomingInvoice && (
                    <Grid container spacing={1}>
                        {renderMetaRow(i18n.totalPrice, (
                            <Typography>{formatter.format(subscriptionDetail.upcomingInvoice.amount_due / 100)}</Typography>
                        ))}
                        {subscriptionDetail.upcomingInvoice?.next_payment_attempt && (
                            renderMetaRow(i18n.subscriptionUpInvoiceDueDate, (
                                <Typography>{moment.unix(subscriptionDetail.upcomingInvoice.next_payment_attempt).format("DD.MM.YYYY")}</Typography>
                            ))
                        )}
                    </Grid>
                )}
                {subscriptionDetail.subscriptionInfos.cancel_at && (
                    <Grid container spacing={1}>
                        {renderMetaRow(i18n.subscriptionCanceledAt, (
                            <Typography>{moment.unix(subscriptionDetail.subscriptionInfos.cancel_at).format("DD.MM.YYYY")}</Typography>
                        ))}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {i18n.cancel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionDetailDialog;
